import type { StackProps } from '@hover/blueprint';
import { Box, Flex, forwardRef } from '@hover/blueprint';
import React from 'react';

import { isOriginalImageDisplay } from 'src/modules/homeownerApp/helpers';
import type { ButtonProps } from 'src/shared/components';
import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';

import { isAiDisplay } from '../../types';
import type { CustomizeButtonProps } from '../CustomizeButton';
import { CustomizeButton } from '../CustomizeButton';
import { SaveButton } from '../SaveButton';
import { ShareButton } from '../ShareButton';

export interface MobileThumbnailEngagementButtonsProps extends StackProps {
  customizeButtonProps?: Partial<CustomizeButtonProps>;
  image: AiDisplayImageFragment;
  isDisabled?: boolean;
}

const buttonClassName = 'thumbnail-engagment-button';
const iconButtonClassName = 'thubmnail-engagement-icon-button';

/**
 * These selectors are made available in order to target parts of this component
 * from higher up in the DOM tree when we can't use more idiomatic Chakra or
 * Emotion styling techniques (i.e: when using a container query)
 *
 * @see {@link ../GeneratedImageThumbnail.tsx} for an example
 */
export const selectors = {
  button: `.${buttonClassName}`,
  iconButton: `.${iconButtonClassName}`,
};

const iconButtonProps = {
  shape: 'circle',
  variant: 'secondary',
  className: iconButtonClassName,
} satisfies ButtonProps;

export const MobileThumbnailEngagementButtons = forwardRef<
  MobileThumbnailEngagementButtonsProps,
  'div'
>(({ customizeButtonProps, image, isDisabled, ...props }, ref) => (
  <Flex
    alignItems="center"
    gap={200}
    {...props}
    flex={1}
    justifyContent="space-between"
    ref={ref}
  >
    <CustomizeButton
      className={buttonClassName}
      image={image}
      isDisabled={isDisabled}
      variant="secondary"
      {...customizeButtonProps}
    />
    {isAiDisplay(image) && !isOriginalImageDisplay(image) && (
      <Box gap={200}>
        <ShareButton
          aiDisplayImage={image}
          {...iconButtonProps}
          isDisabled={isDisabled}
        />
        <SaveButton
          colorScheme="dark"
          image={image}
          {...iconButtonProps}
          isDisabled={isDisabled}
        />
      </Box>
    )}
  </Flex>
));
